<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <header class="relative flex h-16 shrink-0 items-center gap-2 border-b border-default bg-default px-3 sm:px-5">
        <UButton
            :aria-label="$t('nav.menu')"
            class="lg:hidden"
            color="neutral"
            variant="ghost"
            icon="i-lucide-menu"
            size="lg"
            @click="toggleMobileMenu"
        />

        <span class="truncate text-base font-semibold text-highlighted lg:hidden">Imprion PMS</span>

        <!-- The property every screen works in. A receptionist works in one hotel all
             day, so this is a quiet setting rather than a filter to re-pick — but it
             has to be visible, because every screen below silently depends on it.

             The test id is there because the `aria-label` beside it does not reach the
             control: `USelect` renders its own button and the attribute stays on the
             wrapper, so the switcher has no accessible NAME and a browser test has
             nothing to find it by. e2e/specs/12-transfers.spec.ts is the only reader —
             it is the one spec that works in both houses. -->
        <USelect
            v-if="properties.length > 1"
            v-model="propertyId"
            :items="propertyOptions"
            :aria-label="$t('property.property')"
            icon="i-lucide-building-2"
            data-testid="property-switcher"
            class="ms-2 w-44 sm:w-56"
            @update:model-value="selectProperty"
        />
        <span v-else-if="properties.length === 1" class="ms-2 hidden truncate text-sm text-muted sm:inline">
            {{ properties[0].name }}
        </span>

        <div class="ms-auto flex items-center gap-2">
            <!-- The name was inert text. It is now the way to one's own profile —
                 where a person says where to send their rota (docs/OBVESCANJE.md §2).
                 There is no menu entry for it on purpose: it is not a screen anybody
                 navigates TO, it is the thing under your own name. -->
            <RouterLink
                v-if="userName"
                :to="{ name: 'profile' }"
                class="hidden max-w-40 truncate text-sm text-muted hover:text-highlighted sm:inline"
                :title="$t('profile.nav')"
            >
                {{ userName }}
            </RouterLink>

            <!-- What is waiting for this person. It stands before the settings
                 buttons because it is the only control up here that is ever about
                 work, and after the name because it is about that name. -->
            <NotificationBell />

            <!-- The assistant, which used to be a menu entry three folds down. The
                 bell and Milo's head are the pair of things up here that are about
                 work rather than about the browser, so they sit together; on a narrow
                 window the name beside them hides and the head does not.
                 Nothing is drawn at all where the account has no chat:use — see the
                 drawer's `available`, which is the same test. -->
            <!-- Not drawn while the drawer is open: the drawer shows the same head, large,
                 and two Milos on one screen read as two assistants. The button's job
                 (opening him) is done; closing is the drawer's own cross. -->
            <MiloHead
                v-if="assistantAvailable && !open"
                :state="orbState"
                @click="toggleDrawer"
            />

            <AssistantDrawer />

            <!-- The gear, and the only way to the settings screens now that they have
                 left the menu. It is here rather than in the sidebar because that is
                 where a person looks for it in every other application they use, and
                 because a list of twenty-two entries a house opens a few times a year
                 was reading all day in a menu that is for the shift. -->
            <UButton
                :to="{ name: 'settings' }"
                :label="$t('nav.settings')"
                :aria-label="$t('nav.settings')"
                :title="$t('nav.settings')"
                icon="i-lucide-settings"
                :ui="{ label: 'hidden sm:inline' }"
                :color="settingsActive ? 'primary' : 'neutral'"
                :variant="settingsActive ? 'subtle' : 'ghost'"
                size="lg"
                data-testid="topbar-settings"
                @click="closeMobileMenu"
            />

            <UButton
                :label="themeLabel"
                :aria-label="`${$t('nav.theme')}: ${themeLabel}`"
                :title="`${$t('nav.theme')}: ${themeLabel}`"
                :icon="themeIcon"
                :ui="{ label: 'hidden sm:inline' }"
                color="neutral"
                variant="ghost"
                size="lg"
                @click="cycleTheme"
            />

            <!-- The interface language is the person's, not the hotel's: two receptionists
                 on the same desk can want different ones, so it is remembered per browser
                 and never touches what the guest is written to. -->
            <UButton
                :label="languageLabel"
                :aria-label="`${$t('nav.language')}: ${languageName}`"
                :title="`${$t('nav.language')}: ${languageName}`"
                :ui="{ label: 'font-semibold' }"
                color="neutral"
                variant="ghost"
                icon="i-lucide-languages"
                size="lg"
                @click="cycleLanguage"
            />

            <UButton
                :label="$t('nav.logout')"
                :aria-label="$t('nav.logout')"
                :ui="{ label: 'hidden sm:inline' }"
                color="neutral"
                variant="ghost"
                icon="i-lucide-log-out"
                size="lg"
                @click="logout"
            />
        </div>

        <!-- Non-blocking progress hint: the desk keeps reading the screen while a
             request is in flight, so loading never takes the page away. -->
        <div
            v-if="appStore.isLoaderActive()"
            class="absolute inset-x-0 bottom-0 h-0.5 animate-pulse bg-primary"
        />
    </header>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import AssistantDrawer from '@/components/app/AssistantDrawer.vue'
    import MiloHead from '@/components/app/MiloHead.vue'
    import NotificationBell from '@/layouts/components/NotificationBell.vue'
    import { useAssistant } from '@/libs/assistant'
    import { useLayout } from '@/layouts/layout'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import type { IProperty } from '@/types/Property'

    const { t }      = useI18n()
    const route      = useRoute()
    const router     = useRouter()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const properties = ref<IProperty[]>([])
    const propertyId = ref(appStore.propertyId)

    const { orbState, toggleDrawer, open } = useAssistant()

    const assistantAvailable = computed((): boolean => appStore.can('chat:use'))

    const propertyOptions = computed(() =>
        properties.value.map(property => ({ label: property.name, value: property.id })))

    const selectProperty = (id: string) => {
        appStore.setPropertyId(id)
        // A full reload rather than a reactive cascade: every open screen was loaded
        // for the old property, and half-refreshed data about two hotels at once is
        // exactly the confusion this selector exists to prevent.
        window.location.reload()
    }

    // Loaded here rather than per screen: without a property chosen, every screen shows
    // "choose a property first", which on a single-hotel install is a dead end nobody
    // should have to walk out of. One property selects itself.
    onMounted(async () => {
        if (!appStore.isLoggedIn()) return

        const { statusCode, data } = await fetchStore.fetch('/properties').get().json<IProperty[]>()

        if (statusCode.value !== 200 || !data.value) return

        properties.value = data.value

        const known = properties.value.some(property => property.id === appStore.propertyId)

        if (!known && properties.value.length) {
            propertyId.value = properties.value[0].id
            appStore.setPropertyId(propertyId.value)
        }
    })

    const { toggleMobileMenu, closeMobileMenu } = useLayout()

    /** The gear is lit while anything under it is on screen. */
    const settingsActive = computed((): boolean => route.path.startsWith('/settings'))

    const userName = computed(() => appStore.userData?.name ?? appStore.userData?.username ?? '')

    // Three states, one button: Light → Dark → System → Light. The current one is named
    // on the button itself, not only in a tooltip — on a touch screen there is no hover.
    const themeOrder = ['light', 'dark', 'auto'] as const

    const themeIcon = computed(() => {
        if (appStore.colorMode === 'light') return 'i-lucide-sun'
        if (appStore.colorMode === 'dark')  return 'i-lucide-moon'

        return 'i-lucide-monitor'
    })

    const themeLabel = computed(() => {
        if (appStore.colorMode === 'light') return t('nav.theme_light')
        if (appStore.colorMode === 'dark')  return t('nav.theme_dark')

        return t('nav.theme_system')
    })

    const cycleTheme = () => {
        const current = themeOrder.indexOf(appStore.colorMode as typeof themeOrder[number])

        appStore.colorMode = themeOrder[(current + 1) % themeOrder.length]
    }

    // Two languages, so one button that swaps them rather than a select to open. The
    // button shows the code it is on now; the full name is on the tooltip and for a
    // screen reader, because "SL" alone is not a word.
    const languages = ['sl', 'en'] as const

    const languageLabel = computed(() => appStore.locale.toUpperCase())

    const languageName = computed(() => t(`nav.language_${appStore.locale}`))

    const cycleLanguage = () => {
        const current = languages.indexOf(appStore.locale as typeof languages[number])

        appStore.changeLocale(languages[(current + 1) % languages.length])
    }

    const logout = () => {
        closeMobileMenu()
        appStore.setLogout()
        router.push({ name: 'login' })
    }
</script>
